import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import API from '../api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const PostDetail = () => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const { id } = useParams();

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await API.get(`/posts/${id}`);
      setPost(response.data);
    } catch (error) {
      toast.error('Failed to fetch post');
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!user) {
      toast.error('Please login to like posts');
      return;
    }

    try {
      const response = await API.post(`/posts/${id}/like`);
      setPost(response.data);
    } catch (error) {
      toast.error('Failed to like post');
      console.error('Error liking post:', error);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to comment');
      return;
    }

    if (!comment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    setSubmitting(true);
    try {
      const response = await API.post(`/posts/${id}/comments`, {
        text: comment
      });
      setPost(response.data);
      setComment('');
      toast.success('Comment added successfully!');
    } catch (error) {
      toast.error('Failed to add comment');
      console.error('Error adding comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="text-center mt-3">Loading post...</div>;
  }

  if (!post) {
    return <div className="text-center mt-3">Post not found</div>;
  }

  const isLiked = user && post.likes.includes(user.id);

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <div className="card">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h1>{post.title}</h1>
          <small style={{ color: '#666' }}>
            {formatDate(post.createdAt)}
          </small>
        </div>

        <div className="mb-3">
          <span style={{ color: '#666' }}>
            By <Link to={`/user/${post.author._id}`}>{post.author.username}</Link>
          </span>
        </div>

        {post.tags && post.tags.length > 0 && (
          <div className="mb-3">
            {post.tags.map((tag, index) => (
              <span 
                key={index}
                style={{
                  backgroundColor: '#e9ecef',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '3px',
                  fontSize: '0.875rem',
                  marginRight: '0.5rem'
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <div style={{ 
          lineHeight: '1.6', 
          fontSize: '1.1rem',
          marginBottom: '2rem'
        }}>
          {post.content}
        </div>

        <div className="d-flex justify-content-between align-items-center">
          <div>
            <button 
              onClick={handleLike}
              className="btn"
              style={{
                background: 'none',
                border: 'none',
                color: isLiked ? '#dc3545' : '#666',
                fontSize: '1.2rem'
              }}
            >
              ❤️ {post.likes.length}
            </button>
            <span style={{ marginLeft: '1rem', color: '#666' }}>
              💬 {post.comments.length} comments
            </span>
          </div>

          {user && post.author._id === user.id && (
            <div>
              <Link to={`/edit-post/${post._id}`} className="btn btn-secondary me-2">
                Edit
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Comments Section */}
      <div className="card mt-4">
        <h3>Comments</h3>
        
        {user ? (
          <form onSubmit={handleComment} className="mb-4">
            <div className="form-group">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="form-control"
                rows="3"
                placeholder="Write a comment..."
                maxLength="1000"
              />
            </div>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Posting...' : 'Post Comment'}
            </button>
          </form>
        ) : (
          <p className="text-center mb-4">
            <Link to="/login">Login</Link> to leave a comment
          </p>
        )}

        {post.comments.length === 0 ? (
          <p className="text-center text-muted">No comments yet. Be the first to comment!</p>
        ) : (
          <div>
            {post.comments.map((comment, index) => (
              <div key={index} className="border-bottom pb-3 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>{comment.user.username}</strong>
                  <small style={{ color: '#666' }}>
                    {formatDate(comment.createdAt)}
                  </small>
                </div>
                <p style={{ margin: 0 }}>{comment.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PostDetail; 